/**
 * `/admin/input-data` — ручной ввод и обновление показателей паспорта.
 *
 * Состав формы страница НЕ знает: она запрашивает его у сервера
 * (`/api/supervisor/passport-form`), который собирает форму из реестра
 * показателей — того же, что питает Excel-шаблон. Поэтому подписи, единицы,
 * состав по уровню территории и адреса записи всегда совпадают между двумя
 * способами ввода, а страница остаётся тонкой: разделы, фильтры, правки,
 * сохранение.
 *
 * Кто может править: супер-администратор (любая территория) и ответственное
 * лицо КАТО (только свои). Проверка — на сервере; здесь UX-слой, который просто
 * не показывает лишнего.
 *
 * Показателей много, поэтому: слева разделы со счётчиком заполненности, справа
 * карточки одного раздела, сверху поиск и фильтр «только незаполненные», снизу
 * панель с числом правок. Уйти со страницы с несохранёнными правками браузер не
 * даст молча.
 *
 * Источник показателя (US-39, DP-545): у каждого показателя бейдж слоя, у
 * показателя с объявленным источником — тумблер из до трёх вариантов; ввод
 * открыт только на «Ручном вводе». Что показывать после переключения —
 * значение, бейдж, подпись — знает только сервер, поэтому переключённый
 * показатель перечитывается из формы, а прочие несохранённые правки остаются.
 */
import { Check, Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { useUser } from '../../../contexts/UserContext';
import { cn } from '../../../lib/cn';
import { canEditData, katoCovers } from '../../../lib/user-access';
import {
  getPassportForm,
  savePassportForm,
  setSourceMode,
  type PassportBlock,
  type PassportField,
  type PassportForm,
  type PassportSection,
  type SaveIssue,
} from '../../../requests/input_data.request';
import { getKatoList, type KatoListItem } from '../../../requests/public.request';
import { AkimPhotoCard } from './components/AkimPhotoCard';
import { BlockCard } from './components/BlockCard';
import { FieldsCard } from './components/FieldsCard';
import { TemplatePanel } from './components/TemplatePanel';
import {
  buildState,
  changedBlocks,
  dirtyCount,
  emptyRow,
  pick,
  sectionProgress,
  toInput,
  toSaveRequest,
  type BlockState,
  type FormState,
  type Lang,
} from './formState';
import {
  MANUAL,
  isLocked,
  legacySnpOption,
  modeOf,
  modesOf,
  sourceStateOf,
} from './sources';

/** Сколько лет можно выбрать назад от текущего. */
const YEARS_BACK = 4;

/**
 * Единственное поле-изображение, известное ДО появления `is_image` в контракте.
 * Нужен, пока в проде может стоять бэкенд без этого признака: репозитории
 * деплоятся порознь, и фронт обязан пережить старый бэкенд. Удалить, когда
 * `is_image` раскатан во всех регионах.
 */
const LEGACY_IMAGE_KEY = 'general.akim_image_id';

/**
 * Поле-изображение: в контракте это строковый показатель реестра (id блоба в
 * хранилище), но вводится он файлом — см. `AkimPhotoCard`, — поэтому из общего
 * списка вынимается и текстовым полем не показывается.
 *
 * Признак приходит с сервера (`is_image` в реестре показателей), а не узнаётся
 * по ключу: то же знание о поле фронт и бэкенд держали в трёх местах сразу, и
 * добавить второе изображение значило бы вписать его ключ во все (DP-461).
 *
 * На ключ откатываемся, только если сервер признака не прислал ВООБЩЕ. Новый
 * бэкенд шлёт его каждому полю, в том числе `false`, — тогда до ключа дело не
 * доходит и фолбэк сам себя отключает.
 */
const isImageField = (field: PassportField): boolean =>
  field.is_image ?? field.key === LEGACY_IMAGE_KEY;

const EMPTY_STATE: FormState = { scalars: {}, blocks: {} };

/** Элемент формы по ключу: показатель (`field.key`) либо блок (`block.id`). */
const findEntry = (
  form: PassportForm,
  key: string,
): { field: PassportField } | { block: PassportBlock } | null => {
  for (const section of form.sections) {
    const field = section.fields.find((candidate) => candidate.key === key);
    if (field !== undefined) return { field };
    const block = section.blocks.find((candidate) => candidate.id === key);
    if (block !== undefined) return { block };
  }
  return null;
};

/** Заменить в форме один элемент на его свежую версию с сервера. */
const replaceEntry = (form: PassportForm, fresh: PassportForm, key: string): PassportForm => {
  const found = findEntry(fresh, key);
  if (found === null) return form;
  return {
    ...form,
    sections: form.sections.map((section) => ({
      ...section,
      fields:
        'field' in found
          ? section.fields.map((field) => (field.key === key ? found.field : field))
          : section.fields,
      blocks:
        'block' in found
          ? section.blocks.map((block) => (block.id === key ? found.block : block))
          : section.blocks,
    })),
  };
};

/** Снимок одного элемента для сравнения «оператор успел поправить или нет». */
const entrySignature = (state: FormState, key: string): string =>
  JSON.stringify(
    key in state.blocks
      ? (state.blocks[key]?.rows ?? []).map((row) => row.cells)
      : (state.scalars[key] ?? null),
  );

/** Плоский список территорий с отступом по глубине — иначе округа не различить. */
const flatten = (items: KatoListItem[]): { item: KatoListItem; depth: number }[] => {
  const children = new Map<number | null, KatoListItem[]>();
  for (const item of items) {
    const bucket = children.get(item.parent_id);
    if (bucket) bucket.push(item);
    else children.set(item.parent_id, [item]);
  }
  const out: { item: KatoListItem; depth: number }[] = [];
  const walk = (parent: number | null, depth: number) => {
    for (const item of children.get(parent) ?? []) {
      out.push({ item, depth });
      walk(item.kato, depth + 1);
    }
  };
  walk(null, 0);
  const known = new Set(items.map((i) => i.kato));
  for (const item of items) {
    if (item.parent_id !== null && !known.has(item.parent_id)) out.push({ item, depth: 0 });
  }
  return out;
};

export const InputData = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const lang: Lang = i18n.language === 'kz' ? 'kz' : 'ru';

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [kato, setKato] = useState<number | null>(null);
  const [katoItems, setKatoItems] = useState<KatoListItem[]>([]);
  const [form, setForm] = useState<PassportForm | null>(null);
  const [state, setState] = useState<FormState>(EMPTY_STATE);
  const [initial, setInitial] = useState<FormState>(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [query, setQuery] = useState('');
  const [emptyOnly, setEmptyOnly] = useState(false);
  // Выбор источника по ключу элемента формы — приходит с сервера вместе с
  // формой, меняется тумблером (US-39 FR-12).
  const [modes, setModes] = useState<Record<string, string | null>>({});
  // Почему значения не сохранены — по ключу, готовым текстом сервера.
  const [issues, setIssues] = useState<Record<string, string>>({});
  // Территория и год на экране: ответ, пришедший по прежним, не применяется.
  const shownRef = useRef<{ kato: number | null; year: number }>({ kato: null, year });
  shownRef.current = { kato, year };

  const isSuperAdmin = user?.is_admin ?? false;
  const ownKatos = useMemo(
    () => (user?.kato_access ?? []).filter((code) => code !== 0),
    [user],
  );
  // Право ввода, а не наличие территории: «только просмотр» тоже имеет КАТО.
  const canEdit = canEditData(user);

  // Справочник территорий: супер-админу — весь, ответственному лицу — его КАТО
  // вместе с подчинённой иерархией (по остальным сервер всё равно откажет).
  // `ownKatos` — корни поддеревьев, поэтому сравнение через `includes` спрятало
  // бы от ответственного за район все сёла внутри этого района.
  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    getKatoList()
      .then((response) => {
        if (cancelled) return;
        const items = isSuperAdmin
          ? response.items
          : response.items.filter((item) =>
            ownKatos.some((root) => katoCovers(root, item.kato)));
        setKatoItems(items);
        setKato((current) => current ?? items[0]?.kato ?? ownKatos[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setKato((current) => current ?? ownKatos[0] ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [canEdit, isSuperAdmin, ownKatos]);

  const reload = useCallback(async (targetKato: number, targetYear: number) => {
    setLoading(true);
    setFailed(false);
    try {
      const loaded = await getPassportForm(targetKato, targetYear);
      setForm(loaded);
      setState(buildState(loaded));
      // Отдельный снимок: сравнение «изменилось / нет» не должно видеть правки.
      setInitial(buildState(loaded));
      // Выбор источника приезжает вместе с формой — он про эту территорию и год.
      setModes(modesOf(loaded));
      setIssues({});
      setActiveSection(0);
    } catch {
      setFailed(true);
      setForm(null);
      setState(EMPTY_STATE);
      setInitial(EMPTY_STATE);
      setModes({});
      setIssues({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (kato === null) return;
    void reload(kato, year);
  }, [kato, year, reload]);

  const dirty = useMemo(() => dirtyCount(state, initial), [state, initial]);

  useEffect(() => {
    if (dirty === 0) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // --- правки -----------------------------------------------------------
  /** Правка снимает причину отказа: оператор её исправляет. */
  const clearIssue = (key: string) => {
    setIssues((current) => {
      if (!(key in current)) return current;
      const rest = { ...current };
      delete rest[key];
      return rest;
    });
  };

  const setScalar = (key: string, value: string) => {
    clearIssue(key);
    setState((current) => ({
      ...current,
      scalars: { ...current.scalars, [key]: value },
    }));
  };

  const setCell = (blockId: string, uid: string, column: string, value: string) => {
    setState((current) => ({
      ...current,
      blocks: {
        ...current.blocks,
        [blockId]: {
          rows: (current.blocks[blockId]?.rows ?? []).map((row) =>
            row.uid === uid ? { ...row, cells: { ...row.cells, [column]: value } } : row,
          ),
        },
      },
    }));
  };

  const addRow = (blockId: string) => {
    const block = form?.sections
      .flatMap((section) => section.blocks)
      .find((candidate) => candidate.id === blockId);
    if (block === undefined) return;
    setState((current) => ({
      ...current,
      blocks: {
        ...current.blocks,
        [blockId]: { rows: [...(current.blocks[blockId]?.rows ?? []), emptyRow(block)] },
      },
    }));
  };

  const removeRow = (blockId: string, uid: string) => {
    setState((current) => ({
      ...current,
      blocks: {
        ...current.blocks,
        [blockId]: {
          rows: (current.blocks[blockId]?.rows ?? []).filter((row) => row.uid !== uid),
        },
      },
    }));
  };

  /** Подставить в состояние один элемент из снимка — значение показателя или строки блока. */
  const putEntry = (target: FormState, key: string, from: FormState): FormState =>
    key in from.blocks
      ? {
          ...target,
          blocks: {
            ...target.blocks,
            [key]: {
              rows: (from.blocks[key]?.rows ?? []).map((row) => ({
                ...row,
                cells: { ...row.cells },
              })),
            } satisfies BlockState,
          },
        }
      : { ...target, scalars: { ...target.scalars, [key]: from.scalars[key] ?? '' } };

  /**
   * Значение элемента на «Ручном вводе» — то, что оператор ввёл раньше (FR-13).
   * Слой ручного ввода хранится отдельно и при переключении не теряется: под
   * «Ручной ввод» в поле встаёт введённое оператором, а не значение источника.
   */
  const manualSnapshot = (key: string): FormState | null => {
    if (form === null) return null;
    const found = findEntry(form, key);
    if (found === null) return null;
    if ('field' in found) {
      const { field } = found;
      // До US-39 сервер мог не прислать `manual_value` — тогда в поле остаётся
      // то, что в нём было.
      if (field.manual_value === undefined) return null;
      return {
        scalars: { [key]: toInput(field.manual_value, field.dtype) },
        blocks: {},
      };
    }
    const { block } = found;
    if (block.manual_rows === undefined) return null;
    return buildState({
      ...form,
      sections: [
        { title_ru: '', title_kz: '', fields: [], blocks: [{ ...block, rows: block.manual_rows ?? [] }] },
      ],
    });
  };

  /**
   * Переключить источник показателя или блока.
   *
   * Выбор хранит сервер (`source_mode`, ключ «показатель × КАТО × год»), поэтому
   * запрос уходит сразу, не дожидаясь «Сохранить»: он определяет, что оператор
   * может править прямо сейчас. Интерфейс переключается оптимистично, а на отказе
   * откатывается целиком — и тумблер, и значение: иначе форма показывала бы
   * состояние, которого на сервере нет.
   *
   * После ответа элемент перечитывается из формы: какое значение у выбранного
   * слоя, какой бейдж и закрыто ли поле, решает сервер (US-39 FR-4, FR-11).
   * Правка, которую оператор успел начать до ответа, не затирается.
   */
  const changeSource = (key: string, mode: string) => {
    if (kato === null || form === null) return;
    const requestKato = kato;
    const requestYear = year;
    const previousMode = modes[key] ?? null;
    const previousState = state;
    const previousInitial = initial;

    // Под «Ручной ввод» встаёт введённое оператором; под источник — то, что
    // сервер прислал как значение источника, пока не придёт свежее.
    const snapshot = mode === MANUAL ? manualSnapshot(key) : initial;
    const optimisticState = snapshot !== null ? putEntry(state, key, snapshot) : state;
    const optimisticInitial = snapshot !== null ? putEntry(initial, key, snapshot) : initial;
    const optimisticSignature = entrySignature(optimisticState, key);
    setModes((current) => ({ ...current, [key]: mode }));
    setState(optimisticState);
    setInitial(optimisticInitial);
    clearIssue(key);

    const stillShown = () =>
      shownRef.current.kato === requestKato && shownRef.current.year === requestYear;

    void setSourceMode(requestKato, requestYear, key, mode)
      .then(async () => {
        const fresh = await getPassportForm(requestKato, requestYear);
        if (!stillShown()) return;
        const found = findEntry(fresh, key);
        if (found === null) return;
        const freshState = buildState(fresh);
        setForm((current) => (current === null ? current : replaceEntry(current, fresh, key)));
        setModes((current) => ({
          ...current,
          [key]: modeOf('field' in found ? found.field : found.block),
        }));
        setInitial((current) => putEntry(current, key, freshState));
        setState((current) =>
          entrySignature(current, key) === optimisticSignature
            ? putEntry(current, key, freshState)
            : current,
        );
      })
      .catch(() => {
        if (!stillShown()) return;
        setModes((current) => ({ ...current, [key]: previousMode }));
        setState((current) => putEntry(current, key, previousState));
        setInitial((current) => putEntry(current, key, previousInitial));
        toast.error(t('adminInput.sourceFailed'));
      });
  };

  // --- фильтрация -------------------------------------------------------
  const normalizedQuery = query.trim().toLowerCase();
  const filtering = normalizedQuery !== '' || emptyOnly;

  const filterSection = useCallback(
    (section: PassportSection): PassportSection => {
      const matches = (ru: string, kz: string) =>
        normalizedQuery === ''
        || pick(ru, kz, lang).toLowerCase().includes(normalizedQuery);
      return {
        ...section,
        fields: section.fields.filter(
          (field) =>
            matches(field.label_ru, field.label_kz)
            && (!emptyOnly || (state.scalars[field.key] ?? '') === ''),
        ),
        blocks: section.blocks.filter(
          (block) =>
            matches(block.label_ru, block.label_kz)
            && (!emptyOnly || (state.blocks[block.id]?.rows.length ?? 0) === 0),
        ),
      };
    },
    [normalizedQuery, emptyOnly, lang, state],
  );

  const visible = useMemo(() => {
    if (form === null) return [];
    const indexed = form.sections.map((section, index) => ({ section, index }));
    const chosen = filtering
      ? indexed
      : indexed.filter((entry) => entry.index === activeSection);
    return chosen
      .map((entry) => ({ index: entry.index, section: filterSection(entry.section) }))
      .filter((entry) => entry.section.fields.length > 0 || entry.section.blocks.length > 0);
  }, [form, filtering, activeSection, filterSection]);

  // --- сохранение -------------------------------------------------------
  const issuesOf = (list: SaveIssue[]): Record<string, string> =>
    Object.fromEntries(
      list.map((issue) => [issue.key, pick(issue.message_ru, issue.message_kz, lang)]),
    );

  const handleSave = async () => {
    if (form === null || kato === null) return;
    setSaving(true);
    try {
      const result = await savePassportForm(toSaveRequest(form, state, initial));
      const errors = result.errors ?? [];
      const rejected = result.rejected ?? [];
      // Ошибка проверки откатывает сохранение ЦЕЛИКОМ: правки остаются в форме,
      // у полей — причина.
      if (errors.length > 0) {
        setIssues(issuesOf(errors));
        toast.error(t('adminInput.saveInvalid', { count: errors.length }));
        return;
      }
      toast.success(
        t('adminInput.saved', { count: result.saved_fields + result.saved_blocks }),
      );
      await reload(kato, year);
      // Показатели на источнике не записаны, остальное сохранено (US-39 FR-11):
      // форма перечитана, причина остаётся у поля.
      if (rejected.length > 0) {
        setIssues(issuesOf(rejected));
        toast.warning(t('adminInput.saveRejected', { count: rejected.length }));
      }
    } catch {
      toast.error(t('adminInput.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // --- ветки доступа ----------------------------------------------------
  if (user === null) {
    return <p data-testid="input-data-loading">{t('common.loading')}</p>;
  }
  if (!canEdit) {
    return (
      <p className="p-5 text-sm text-text-muted" data-testid="input-data-forbidden">
        {t('adminInput.forbidden')}
      </p>
    );
  }

  const katoOptions = flatten(katoItems);
  const yearOptions = Array.from({ length: YEARS_BACK + 2 }, (_, i) => currentYear + 1 - i);
  const changedBlockIds = new Set(changedBlocks(state, initial));

  // Варианты прежнего двоичного тумблера — только для бэкенда до US-39.
  const legacyOptions = {
    snp: legacySnpOption(
      i18n.getFixedT('ru')('adminInput.sourceSnp'),
      i18n.getFixedT('kz')('adminInput.sourceSnp'),
    ),
    manual: {
      value: MANUAL,
      label_ru: i18n.getFixedT('ru')('adminInput.sourceManual'),
      label_kz: i18n.getFixedT('kz')('adminInput.sourceManual'),
    },
  };
  const currentUserId = user.user_id;
  const blockLabels = {
    addRow: t('adminInput.addRow'),
    removeRow: t('adminInput.removeRow'),
    empty: t('adminInput.blockEmpty'),
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="input-data">
      <div className="flex flex-wrap items-end gap-4 border-b border-surface-border bg-white px-5 py-4">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold">{t('adminInput.title')}</h1>
          <p className="text-sm text-text-muted">{t('adminInput.subtitle')}</p>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('adminInput.territory')}</span>
          <select
            value={kato ?? ''}
            data-testid="input-data-kato"
            onChange={(event) => setKato(Number(event.target.value))}
            className="h-9 max-w-xs rounded-md border border-surface-border px-3"
          >
            {katoOptions.map(({ item, depth }) => (
              <option key={item.kato} value={item.kato}>
                {`${'  '.repeat(depth)}${lang === 'kz' ? item.name_kz : item.name_ru}`}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('adminInput.year')}</span>
          <select
            value={year}
            data-testid="input-data-year"
            onChange={(event) => setYear(Number(event.target.value))}
            className="h-9 rounded-md border border-surface-border px-3 tabular-nums"
          >
            {yearOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('adminInput.search')}</span>
          <span className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-text-muted" />
            <Input
              value={query}
              data-testid="input-data-search"
              placeholder={t('adminInput.searchPlaceholder')}
              onChange={(event) => setQuery(event.target.value)}
              className="h-9 w-64 pl-8"
            />
          </span>
        </label>

        <label className="flex h-9 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={emptyOnly}
            data-testid="input-data-empty-only"
            onChange={(event) => setEmptyOnly(event.target.checked)}
            className="size-4"
          />
          {t('adminInput.emptyOnly')}
        </label>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* На планшете 64 rem-колонки навигации не остаётся ширины под
            форму — до lg колонка уже. */}
        <nav className="w-52 shrink-0 overflow-y-auto border-r border-surface-border bg-white py-2 lg:w-64">
          {(form?.sections ?? []).map((section, index) => {
            const { filled, total } =
              form !== null
                ? sectionProgress(form, state, index)
                : { filled: 0, total: 0 };
            const percent = total === 0 ? null : Math.round((filled / total) * 100);
            return (
              <button
                // Ключ — позиция, а не заголовок: сервер склеивает одноимённые
                // разделы, но React не должен молча ломаться, если совпадение
                // когда-нибудь вернётся (одинаковые ключи → состояние не у того
                // элемента).
                key={index}
                type="button"
                data-testid={`section-${String(index)}`}
                onClick={() => setActiveSection(index)}
                className={cn(
                  'flex w-full flex-col gap-1 px-4 py-2.5 text-left text-sm transition-colors',
                  index === activeSection && !filtering
                    ? 'bg-brand-50 font-semibold text-brand-700'
                    : 'hover:bg-surface-muted',
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">
                    {pick(section.title_ru, section.title_kz, lang)}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-text-muted">
                    {percent === null ? '—' : `${String(percent)}%`}
                  </span>
                </span>
                {percent !== null && (
                  <span className="h-1 w-full overflow-hidden rounded-full bg-surface-muted">
                    <span
                      className="block h-full bg-brand-500"
                      style={{ width: `${String(percent)}%` }}
                    />
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1 overflow-y-auto p-5">
          {kato !== null && !filtering && (
            <div className="mb-5">
              <TemplatePanel
                kato={kato}
                year={year}
                lang={lang}
                onApplied={(message, rejectedBySource) => {
                  toast.success(message);
                  // Ячейки показателей на источнике не записаны (US-39 FR-5c.4).
                  if (rejectedBySource > 0) {
                    toast.warning(
                      t('adminInput.templateRejectedBySource', { count: rejectedBySource }),
                    );
                  }
                  void reload(kato, year);
                }}
              />
            </div>
          )}

          {loading ? (
            <div className="space-y-3" data-testid="input-data-skeleton">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : failed ? (
            <p className="text-sm text-text-muted" data-testid="input-data-failed">
              {t('common.dataUnavailable')}
            </p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-text-muted" data-testid="input-data-nothing">
              {t('adminInput.nothingFound')}
            </p>
          ) : (
            <div className="space-y-6">
              {visible.map(({ section, index }) => (
                <section key={index} className="space-y-3">
                  {filtering && (
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                      {pick(section.title_ru, section.title_kz, lang)}
                    </h2>
                  )}
                  {section.fields
                    .filter(isImageField)
                    .map((field) => (
                      <AkimPhotoCard
                        key={field.key}
                        kato={kato ?? 0}
                        year={year}
                        lang={lang}
                        field={field}
                        onUploaded={(message) => {
                          toast.success(message);
                          if (kato !== null) void reload(kato, year);
                        }}
                      />
                    ))}
                  <FieldsCard
                    // В режиме поиска заголовок раздела уже стоит над карточками
                    // (`<h2>` выше) — второй раз он бы просто дублировался.
                    title={filtering ? '' : pick(section.title_ru, section.title_kz, lang)}
                    fields={section.fields.filter(
                      (field) => !isImageField(field),
                    )}
                    lang={lang}
                    values={state.scalars}
                    initial={initial.scalars}
                    onChange={setScalar}
                    modes={modes}
                    onSourceChange={changeSource}
                    legacyOptions={legacyOptions}
                    currentUserId={currentUserId}
                    issues={issues}
                  />
                  {section.blocks.map((block) => {
                    const source = sourceStateOf(block, legacyOptions);
                    const mode = modes[block.id] ?? source.mode;
                    return (
                      <BlockCard
                        key={block.id}
                        block={block}
                        lang={lang}
                        rows={state.blocks[block.id]?.rows ?? []}
                        changed={changedBlockIds.has(block.id)}
                        onCellChange={(uid, column, value) => {
                          clearIssue(block.id);
                          setCell(block.id, uid, column, value);
                        }}
                        onAddRow={() => addRow(block.id)}
                        onRemoveRow={(uid) => removeRow(block.id, uid)}
                        options={source.options}
                        mode={mode}
                        locked={isLocked(source, mode)}
                        onSourceChange={(next) => changeSource(block.id, next)}
                        currentUserId={currentUserId}
                        issue={issues[block.id]}
                        labels={blockLabels}
                      />
                    );
                  })}
                </section>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-surface-border bg-white px-5 py-3">
        <span className="text-sm text-text-muted" data-testid="input-data-dirty">
          {dirty === 0
            ? t('adminInput.noChanges')
            : t('adminInput.changes', { count: dirty })}
        </span>
        <span className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={dirty === 0 || saving}
            onClick={() => {
              if (form !== null) setState(buildState(form));
              setIssues({});
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={dirty === 0 || saving}
            data-testid="input-data-save"
            onClick={() => void handleSave()}
          >
            {saving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Check className="mr-2 size-4" />
            )}
            {t('common.save')}
          </Button>
        </span>
      </div>
    </div>
  );
};
